import { shelfImages } from "../lib/shelfImagePaths";

const quiet = shelfImages("quiet-shelf", {
  figure1: `${process.env.PUBLIC_URL || ""}/images/quiet-shelf/morning.jpg`,
  figure2: `${process.env.PUBLIC_URL || ""}/images/quiet-shelf/window.jpg`,
  figure3: `${process.env.PUBLIC_URL || ""}/images/quiet-shelf/stack.jpg`,
});

const circles = shelfImages("reader-circles", {
  figure1: `${process.env.PUBLIC_URL || ""}/images/reader-circles/library.jpg`,
  figure2: `${process.env.PUBLIC_URL || ""}/images/reader-circles/stacks.jpg`,
  figure3: `${process.env.PUBLIC_URL || ""}/images/reader-circles/vintage.jpg`,
});

const weekend = shelfImages("weekend-escape", {
  figure1: `${process.env.PUBLIC_URL || ""}/images/weekend-escape/hammock.jpg`,
  figure2: `${process.env.PUBLIC_URL || ""}/images/weekend-escape/open-book.jpg`,
  figure3: `${process.env.PUBLIC_URL || ""}/images/weekend-escape/vintage.jpg`,
});

export const SHELF_ARTICLES = {
  "the-quiet-shelf": {
    images: quiet,
    badge: "Staff Pick",
    title: "The Quiet Shelf",
    author: "Mira Holloway",
    readTime: "8 min read",
    readTag: "Slow living",
    lede:
      "Slow novels for slower Sundays — a gentle manifesto for readers who want their shelves to breathe as much as their calendars do.",
    intro: [
      "There is a particular kind of quiet that only arrives when the phone is face-down, the kettle has finished sighing, and the first chapter waits without hurry. Mira built The Quiet Shelf for that hour — not as a list of must-reads, but as a small room you can step into when the week has been too loud.",
      "Every title here moves at the pace of afternoon light: novels that trust silence, essays that leave margins wide enough for your own thoughts, and stories where nothing explodes unless a teacup does.",
    ],
    quote:
      "“Reading slowly is not falling behind. It is choosing which world deserves your full attention.”",
    sections: [
      {
        heading: "How to read on a",
        highlight: "quiet Sunday",
        body: "Start with one book, not five. Place it where your eyes land naturally — beside the chair, not buried in a stack that accuses you. Light a candle if you like rituals; open a window if you prefer the sound of neighbors living their own stories.",
        figure: {
          key: "figure1",
          alt: "A book resting open on a soft blanket in morning light",
          caption: "Let the first page be the only appointment on your calendar.",
        },
      },
      {
        body: "Keep a pencil nearby, not for underlining every beautiful sentence, but for the rare line that feels like it was written to find you. The Quiet Shelf celebrates dog-eared corners and cracked spines — evidence that a book was lived in, not displayed.",
        figure: {
          key: "figure2",
          alt: "Soft light by a window with a book nearby",
          caption: "Rain optional. Stillness required.",
        },
      },
    ],
    tips: [
      {
        icon: "bookmark",
        title: "One chapter rule",
        text: "Stop while you still want more. Tomorrow’s reading should feel like a reunion, not homework.",
      },
      {
        icon: "coffee",
        title: "Warm drink optional",
        text: "Tea, coffee, or nothing at all. The ritual is the pause, not the mug.",
      },
    ],
    closing: {
      heading: "Three shelves, one mood",
      body: "Inside this collection: Morning Letters for soft epistolary fiction, Long Afternoons for novels that unfold like gardens, and Last Light for endings that make you stare at the ceiling in the best way.",
      figure: {
        key: "figure3",
        alt: "A small stack of worn paperback books",
        caption: "Worn edges welcome. Perfection not required.",
      },
      outro:
        "When you close the last page, leave the book out for a day. That is the spirit of this shelf — reading as a room you are always welcome to return to.",
    },
    cta: {
      title: "Save this shelf to your PagePeers library",
      desc: "Follow Mira’s picks and get a gentle nudge each Sunday when a new quiet read is added.",
    },
    signature: "— Mira Holloway, curator of The Quiet Shelf",
  },

  "reader-circles": {
    images: circles,
    badge: "Trending",
    title: "Reader Circles",
    author: "PagePeers Editors",
    readTime: "6 min read",
    readTag: "Community picks",
    lede:
      "What our community is reading this week — small circles, shared shelves, and recommendations that only come from people who have actually turned the page.",
    intro: [
      "Reader Circles are cozy rooms on PagePeers where members gather around a theme and trade notes like friends at a kitchen table.",
      "Each week, editors spotlight one circle that is especially alive: thoughtful threads, generous spoiler tags, and shelves that grow organically.",
    ],
    quote:
      "“The best book I read this month was not on any bestseller list. It was on my friend’s shelf.”",
    sections: [
      {
        heading: "Inside a",
        highlight: "living circle",
        body: "Picture twelve readers who never meet in person but know each other’s tastes. Someone posts a dog-eared photo; someone else replies with one line that sells the whole novel.",
        figure: {
          key: "figure1",
          alt: "Readers browsing books in a warm library",
          caption: "Every circle starts with curiosity, not credentials.",
        },
      },
      {
        body: "This week’s shelf pulls from Late Train Book Club, Soft Sci-Fi Salon, and Essays at Dusk — twelve titles, four already marked read by half the members.",
        figure: {
          key: "figure2",
          alt: "Stacks of books waiting to be discovered",
          caption: "Trending: character-driven novels with quiet endings.",
        },
      },
    ],
    tips: [
      {
        icon: "message",
        title: "Spoiler-safe threads",
        text: "Chapters get their own lanes so late joiners can catch up without ruining the plot.",
      },
      {
        icon: "sparkles",
        title: "Weekly shelf drops",
        text: "Editors bundle the community’s top votes into one shelf you can save in a single tap.",
      },
    ],
    closing: {
      heading: "How to join your first circle",
      body: "Browse open circles, read the pinned welcome note, and add one book you loved recently. Lurk for a day if you like — reply when a thread sounds like your kind of Sunday.",
      figure: {
        key: "figure3",
        alt: "Vintage books on a wooden shelf",
        caption: "Old favorites welcome. New voices celebrated.",
      },
      outro:
        "Reader Circles are where PagePeers feels least like an app and most like a porch light left on for you.",
    },
    cta: {
      title: "Join a circle on PagePeers",
      desc: "Save this week’s trending shelf and get notified when your circle picks its next read.",
    },
    signature: "— PagePeers Editors",
  },

  "weekend-escape": {
    images: weekend,
    badge: "New",
    title: "Weekend Escape",
    author: "Arjun Mehra",
    readTime: "7 min read",
    readTag: "Page-turners",
    lede:
      "Page-turners for lazy afternoons — stories that pull you in before the kettle boils twice.",
    intro: [
      "Arjun built Weekend Escape for readers who love momentum without burnout: thrillers with heart, romances with teeth, and mysteries that respect your need to nap between chapters.",
      "Nothing here asks for a highlighter. These are books you finish in two sittings, then lend to the friend who texts “anything good?” every Friday.",
    ],
    quote:
      "“A perfect weekend book is one you forget to put down and still feel rested when you finish it.”",
    sections: [
      {
        heading: "The art of a",
        highlight: "lazy afternoon",
        body: "Clear the coffee table, stack two options (not six), and let the first chapter decide. If you are still on the sofa when the light turns gold, you picked correctly.",
        figure: {
          key: "figure1",
          alt: "Reading outdoors on a lazy afternoon",
          caption: "The only agenda is finding out what happens next.",
        },
      },
      {
        body: "This month: a coastal noir with a wry narrator, a bookshop romance, and a heist novel where the crew speaks in literary references.",
        figure: {
          key: "figure2",
          alt: "An open book ready to be read",
          caption: "Start anywhere on the shelf — they all reward momentum.",
        },
      },
    ],
    tips: [
      {
        icon: "bookmark",
        title: "Two-sitting rule",
        text: "Every pick should be finishable between lunch and bedtime.",
      },
      {
        icon: "armchair",
        title: "Couch-first design",
        text: "Big emotions, short chapters, hooks at every section break.",
      },
    ],
    closing: {
      heading: "What to grab first",
      body: "Begin with the coastal noir if you want suspense, the bookshop romance if you want warmth. Save the voicemails novella for when you need something short and bright.",
      figure: {
        key: "figure3",
        alt: "Vintage paperbacks on a shelf",
        caption: "Paperback energy, modern pacing.",
      },
      outro:
        "When Monday arrives, you will feel like you stole a bright piece of the weekend and kept it between the covers.",
    },
    cta: {
      title: "Save Weekend Escape to your shelf",
      desc: "Get Arjun’s next picks the moment a new page-turner lands.",
    },
    signature: "— Arjun Mehra, curator of Weekend Escape",
  },

  "timeless-voices": {
    images: shelfImages("timeless-voices"),
    badge: "Classic",
    title: "Timeless Voices",
    author: "Neha Saxena",
    readTime: "7 min read",
    readTag: "Enduring reads",
    lede:
      "Books that have outlasted the noise — voices that still feel immediate decades after the first printing.",
    intro: [
      "Neha assembled Timeless Voices for readers who want substance without pretension: essays that age well, novels that still start arguments at dinner, memoirs that read like letters.",
      "These are not classics because someone said so. They are books members return to when the feed feels loud.",
    ],
    quote:
      "“A timeless book does not shout about its importance. It simply waits until you are ready to hear it.”",
    sections: [
      {
        heading: "What makes a voice",
        highlight: "timeless",
        body: "Clarity, moral weight, and sentences you want to read aloud. Neha favors authors who trust the reader — no gimmicks, just human stakes rendered with patience.",
        figure: {
          key: "figure1",
          alt: "Rows of books in a warm library",
          caption: "The best classics still feel like discovery.",
        },
      },
      {
        body: "Exile and belonging, work and meaning, love that complicates rather than solves — different continents, same quiet intensity.",
        figure: {
          key: "figure2",
          alt: "Stacks of well-loved books",
          caption: "Worn spines are a feature, not a flaw.",
        },
      },
    ],
    tips: [
      {
        icon: "bookmark",
        title: "One passage a day",
        text: "Read a single page you love before bed. Timeless books reward repetition.",
      },
      {
        icon: "library",
        title: "Read in company",
        text: "Several circles host slow reads of these titles — one chapter at a time.",
      },
    ],
    closing: {
      heading: "Neha’s gateway trio",
      body: "Begin with the slim memoir, the mid-century novel, and the essay collection that reads like a late-night conversation. Save the epic for winter.",
      figure: {
        key: "figure3",
        alt: "Curved library shelves at golden hour",
        caption: "Room for both the canon and the books you defend at parties.",
      },
      outro: "Timeless Voices is an antidote — proof that some stories keep their heat long after the hype cools.",
    },
    cta: {
      title: "Add Timeless Voices to your library",
      desc: "Follow Neha’s shelf and get a monthly note when a new enduring read joins.",
    },
    signature: "— Neha Saxena, curator of Timeless Voices",
  },

  "letters-and-tea": {
    images: shelfImages("letters-and-tea"),
    badge: "Loved",
    title: "Letters & Tea",
    author: "Priya Rao",
    readTime: "6 min read",
    readTag: "Warm prose",
    lede:
      "Quiet essays, memoirs, and warm prose — the shelf you open when you want to feel understood, not instructed.",
    intro: [
      "Priya curates Letters & Tea like a host setting out a pot: unhurried, attentive, generous with silence.",
      "Members call this shelf the porch light of PagePeers: not flashy, always on, reliably kind.",
    ],
    quote:
      "“Some books do not chase you. They sit beside you until you are ready to talk.”",
    sections: [
      {
        heading: "The mood of",
        highlight: "letters",
        body: "Epistolary essays, fragmented memoir, observational pieces that trust white space. Priya groups them by emotional weather: gentle, bittersweet, quietly funny.",
        figure: {
          key: "figure1",
          alt: "Misty forest path with soft light ahead",
          caption: "Walk slowly. The best lines arrive when you do.",
        },
      },
      {
        body: "Pair these reads with tea if you like rituals, or with nothing at all. Underline one sentence and ignore the rest until tomorrow.",
        figure: {
          key: "figure2",
          alt: "An open book in soft natural light",
          caption: "Margin notes welcome. Perfection not required.",
        },
      },
    ],
    tips: [
      {
        icon: "coffee",
        title: "Twenty-minute sips",
        text: "One essay per sitting. Stop mid-piece if it lands — let it echo.",
      },
      {
        icon: "heart",
        title: "Share a line",
        text: "Circles trade favorite sentences like recipes. Lurk, then reply.",
      },
    ],
    closing: {
      heading: "Priya’s comfort stack",
      body: "A memoir in letters, winter essays, and a slim novel about two friends who only meet in cafés. Save them in order — or read the title that finds you first.",
      figure: {
        key: "figure3",
        alt: "Vintage books on a wooden shelf",
        caption: "Soft covers, softer endings.",
      },
      outro: "When the world feels sharp, come back here. Letters & Tea will still be warm.",
    },
    cta: {
      title: "Save Letters & Tea",
      desc: "Get Priya’s picks when a new quiet read is added.",
    },
    signature: "— Priya Rao, curator of Letters & Tea",
  },

  "rainy-day-reads": {
    images: shelfImages("rainy-day-reads"),
    badge: "Cozy",
    title: "Rainy Day Reads",
    author: "PagePeers Club",
    readTime: "5 min read",
    readTag: "Blanket stories",
    lede:
      "Curled-up-under-a-blanket stories — fiction and memoir that pair with grey skies and nowhere to be.",
    intro: [
      "The PagePeers Club built Rainy Day Reads for in-between weather: not quite winter, not quite spring, perfect for losing a chapter while the kettle hums.",
      "Every title earns its place with atmosphere — small towns, found families, mysteries gentle enough to read under fleece.",
    ],
    quote: "“Rain on the glass is ambience. The real shelter is the story.”",
    sections: [
      {
        heading: "What counts as",
        highlight: "cozy",
        body: "Low stakes do not mean low feeling. These books can make you cry on page forty — they just will not bully you for it.",
        figure: {
          key: "figure1",
          alt: "Cozy reading in soft afternoon light",
          caption: "If you forget the weather, the book is working.",
        },
      },
      {
        body: "This month: a seaside inn mystery, a library annex romance, and a novella told entirely in voicemails.",
        figure: {
          key: "figure2",
          alt: "Open book beside a warm drink on soft linen",
          caption: "Latte art optional. Blanket mandatory.",
        },
      },
    ],
    tips: [
      {
        icon: "bookmark",
        title: "Chapter breaks",
        text: "Stop at scene changes, not cliffhangers — rainy days are for ease.",
      },
      {
        icon: "coffee",
        title: "Soundtrack off",
        text: "Let rain or silence do the work. These books carry their own rhythm.",
      },
    ],
    closing: {
      heading: "Club picks for this week",
      body: "Start with the library romance for warmth, the inn mystery for a puzzle without panic. Save the voicemails novella for a short bright afternoon.",
      figure: {
        key: "figure3",
        alt: "A stack of books for an afternoon indoors",
        caption: "Grey sky sold separately.",
      },
      outro:
        "Rainy Day Reads is communal — save the shelf, see what everyone is underlining, and add your own recommendation.",
    },
    cta: {
      title: "Join the Rainy Day shelf",
      desc: "Club members get weekly cozy drops and spoiler-safe threads.",
    },
    signature: "— PagePeers Club",
  },
};

export const SHELF_SLUGS = Object.keys(SHELF_ARTICLES);

/** Card cover URLs keyed by slug (for landing page) */
export const SHELF_CARD_COVERS = Object.fromEntries(
  SHELF_SLUGS.map((slug) => [slug, SHELF_ARTICLES[slug].images.cardCover]),
);
